import { Injectable } from '@angular/core'
import { Sort } from '@angular/material/sort'
import { Subject } from 'rxjs'

import { FilterValues } from '@shared/models/filter-values.model'
import { ScheduledParams, ScheduledParamsParse } from '@shared/models/scheduled-report.model'
import { ReportTypes } from '@shared/models/reports/types'
import { TemperatureByVehicleColumn } from '@app/modules/client/reports/sensors/temperature-by-vehicle-details/shared'
import { TemperatureBySensorColumn } from '@app/modules/client/reports/sensors/temperature-by-sensor-details/shared'

interface ExportMap {
  [key: string]: string
}

const exportMapRevertTBV: ExportMap = {
  deviceSensorId: TemperatureByVehicleColumn.SensorID,
  deviceSensorLabel: TemperatureByVehicleColumn.Label,
}

const exportMapRevertTBS: ExportMap = {
  defaultlabel: TemperatureBySensorColumn.Vehicle,
}

const exportMapRevertFuelSummary: ExportMap = {
  reg_no: 'vehicleRegNo',
  depot: 'vehicleDepot',
  groups: 'vehicleGroups',
}

const exportMapRevertFuelRecord: ExportMap = {
  regNo: 'vehicleRegNo',
  defaultLabel: 'vehicleDefaultLabel',
  model: 'vehicleModel',
  depotName: 'vehicleDepot',
  groupsList: 'vehicleGroups',
  fuelType: 'refueledFuelType',
}

const exportMapRevertSpeeding: ExportMap = {
  regNo: 'vehicleIds',
}

const exportMapRevertDefault: ExportMap = {
  regno: 'vehicleIds',
}

const exportMapRevertDigitalIO: ExportMap = {
  regno: 'vehicleIds',
  defaultlabel: 'defaultLabel',
  depotName: 'depot',
  inputType: 'inputTypeIds',
  startAreasName: 'areaStartId',
  finishAreasName: 'areaFinishId',
}

@Injectable({ providedIn: 'root' })
export class ScheduledReportService {
  params$: Subject<ScheduledParamsParse> = new Subject<ScheduledParamsParse>()

  private filterValues: FilterValues
  private sortValue: string
  private fields: string[]

  setFilterValues(values: FilterValues): void {
    const parsingFilter = Object.assign({}, values)
    Object.entries(parsingFilter).forEach(([key, value]) => {
      if (Array.isArray(value) && !value.length) {
        delete parsingFilter[key]
      }
      if (!value) {
        delete parsingFilter[key]
      }
    })
    delete parsingFilter?.startDate
    delete parsingFilter?.endDate

    this.filterValues = parsingFilter
  }

  setSortValue(value: string): void {
    this.sortValue = value
  }

  setFields(values: string[]): void {
    this.fields = values
  }

  getParams(): ScheduledParams {
    return {
      ...(this.filterValues && { ...this.filterValues }),
      ...(this.sortValue && { sort: this.sortValue }),
      ...(this.fields && { fields: this.fields }),
    } as ScheduledParams
  }

  parsingParams(params: ScheduledParams, reportType: ReportTypes): ScheduledParamsParse {
    const exportMap = this.defaultExportRevert(reportType)

    const cloneParams = Object.assign({}, params)

    const sortValue = this.parsingSortValue(cloneParams.sort)
    const fields = this.parsingQueryFields(cloneParams.fields, exportMap)

    delete cloneParams.sort
    delete cloneParams.fields

    const filterValues = this.parsingQueryFilterValues(cloneParams, fields)

    return {
      fields,
      sortValue,
      filterValues,
    }
  }

  private parsingQueryFields(fields: string[], exportMap: ExportMap): string[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
    const rewriteColumnNames = (field) => exportMap?.[field] || field
    return fields.map(rewriteColumnNames) as string[]
  }

  private parsingSortValue(sortValue: string): Sort {
    if (sortValue?.[0] === '-') {
      return {
        active: sortValue.substring(1),
        direction: 'desc',
      }
    }
    return {
      active: sortValue,
      direction: 'asc',
    }
  }

  private parsingQueryFilterValues(filterValues: FilterValues, fields: string[]): FilterValues {
    const ignoreFields = ['vehicleIds']
    const camelToSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    const cloneFilterValues = Object.assign({}, filterValues)
    const filterValuesParse = {}
    Object.entries(cloneFilterValues).forEach(([key, value]: [string, string | number | (string | number)[]]) => {
      const isRange = key.includes('[gte]') || key.includes('[lt]')
      const isCamel = !fields.includes(key) && !ignoreFields.includes(key) && !isRange
      const isDateRange = isRange && (key.includes('date') || key.includes('Date'))
      if (isDateRange) {
        const rangeKey = key.split('[')[0]
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        if (key.includes('[lt]')) filterValuesParse[rangeKey] = { ...filterValuesParse[rangeKey], endDate: value }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        else if (key.includes('[gte]')) filterValuesParse[rangeKey] = { ...filterValuesParse[rangeKey], startDate: value }
      } else if (isRange) {
        const rangeKey = key.split('[')[0]
        filterValuesParse[key] = value
        filterValuesParse[rangeKey] = filterValuesParse[rangeKey]
          ? `${key}=${value as string | number}&${filterValuesParse[rangeKey] as string | number}`
          : `${key}=${value as string | number}`
      } else if (isCamel) {
        filterValuesParse[camelToSnakeCase(key)] = value
      } else {
        filterValuesParse[key] = value
      }
    })
    return filterValuesParse
  }

  private defaultExportRevert(reportType: ReportTypes): ExportMap {
    switch (reportType) {
      case ReportTypes.FuelSummary:
        return exportMapRevertFuelSummary
      case ReportTypes.FuelRecords:
        return exportMapRevertFuelRecord
      case ReportTypes.Speeding:
        return exportMapRevertSpeeding
      case ReportTypes.TemperatureByVehicle:
        return exportMapRevertTBV
      case ReportTypes.TemperatureBySensor:
        return exportMapRevertTBS
      case ReportTypes.DigitalIO:
        return exportMapRevertDigitalIO
      default:
        return exportMapRevertDefault
    }
  }
}
